import type { ScoreVector } from '../data/dimensions';
import { hiddenTypes, type HiddenType } from '../data/taxonomy';
import type { ScoreEventInput } from './scoring';

const screenshotSignals = new Set(['screenshot_to_friend', 'receipt_mode']);

const readReceiptSignals = new Set([
  'leave_on_read',
  'ghost_then_return',
  'mute_chat',
  'delayed_voice',
  'delayed_response',
  'cooldown'
]);

const groupLawyerSignals = new Set([
  'organize_group',
  'set_boundary',
  'receipt_mode',
  'need_clarity',
  'direct_callout'
]);

const memeSupportSignals = new Set([
  'meme_reply',
  'meme_reply_repair',
  'meme_reply_defense',
  'meme_repair',
  'meme_defense'
]);

const abusiveOrEscalatingMemeSignals = new Set(['meme_escalation', 'polite_venom']);

const replaySignals = new Set([
  'repair_exit',
  'offer_solution',
  'delayed_voice',
  'delayed_response',
  'cooldown',
  'type_delete',
  'accountability'
]);

const plotTwisterSignals = new Set(['plot_twist']);

function hiddenType(typeId: string): HiddenType {
  const type = hiddenTypes.find((candidate) => candidate.typeId === typeId);
  if (!type) {
    throw new Error(`Unknown hidden type: ${typeId}`);
  }
  return type;
}

function eventSignals(event: ScoreEventInput): string[] {
  return [event.gestureId, event.coreEvent, event.secondaryChoiceId, event.sceneId, event.category].filter(
    (signal): signal is string => Boolean(signal)
  );
}

function countSignals(events: ScoreEventInput[], signals: Set<string>, predicate?: (event: ScoreEventInput) => boolean) {
  return events.filter((event) => {
    if (predicate && !predicate(event)) {
      return false;
    }
    return eventSignals(event).some((signal) => signals.has(signal));
  }).length;
}

function isGroupScene(event: ScoreEventInput): boolean {
  return event.category === 'group' || event.sceneId.includes('group') || event.sceneId.includes('meeting');
}

function hasHighConflictOrEmotion(event: ScoreEventInput): boolean {
  return (
    event.coreEvent === 'shake_spiral' ||
    event.coreEvent === 'swipe_down_confront' ||
    event.category === 'pressure' ||
    event.secondaryChoiceId === 'polite_venom' ||
    event.secondaryChoiceId === 'direct_callout'
  );
}

function hasRepairOrReplay(event: ScoreEventInput): boolean {
  return (
    event.coreEvent === 'swipe_right_fix' ||
    event.coreEvent === 'timed_hesitation' ||
    Boolean(event.secondaryChoiceId && replaySignals.has(event.secondaryChoiceId))
  );
}

function resolvesScreenshotArchivist(scores: ScoreVector, events: ScoreEventInput[]): boolean {
  return (
    scores.avoidance >= 55 &&
    scores.socialControl >= 55 &&
    scores.directness <= 45 &&
    countSignals(events, screenshotSignals) >= 2
  );
}

function resolvesReadReceiptGhost(scores: ScoreVector, events: ScoreEventInput[]): boolean {
  return scores.avoidance >= 85 && scores.repairIntent <= 45 && countSignals(events, readReceiptSignals) >= 3;
}

function resolvesGroupChatLawyer(scores: ScoreVector, events: ScoreEventInput[]): boolean {
  return (
    scores.directness >= 75 &&
    scores.boundaryFirmness >= 75 &&
    scores.socialControl >= 65 &&
    countSignals(events, groupLawyerSignals, isGroupScene) >= 3
  );
}

function resolvesMemeOverlord(scores: ScoreVector, events: ScoreEventInput[]): boolean {
  if (scores.memeDefense < 90 || events.length < 3) {
    return false;
  }

  const nonAbusiveMemeChoices = events.filter((event) => {
    const signals = eventSignals(event);
    const isMemeChoice = event.coreEvent === 'double_tap_meme' || signals.some((signal) => memeSupportSignals.has(signal));
    return isMemeChoice && !signals.some((signal) => abusiveOrEscalatingMemeSignals.has(signal));
  }).length;

  return nonAbusiveMemeChoices / events.length >= 2 / 3;
}

function resolvesMidnightReplay(scores: ScoreVector, events: ScoreEventInput[]): boolean {
  if (scores.emotionalVolatility < 70 || scores.repairIntent < 75) {
    return false;
  }

  let highEmotionSeen = false;
  let replayAfterEmotion = 0;
  for (const event of events) {
    if (hasRepairOrReplay(event) && highEmotionSeen) {
      replayAfterEmotion += 1;
    }
    if (hasHighConflictOrEmotion(event)) {
      highEmotionSeen = true;
    }
  }

  return replayAfterEmotion >= 2;
}

function resolvesPlotTwister(scores: ScoreVector, events: ScoreEventInput[]): boolean {
  const dramaSignals = countSignals(events, plotTwisterSignals);

  return scores.chaosAppetite >= 72 && scores.impulsiveness >= 70 && dramaSignals >= 3;
}

export function resolveHiddenType(scores: ScoreVector, events: ScoreEventInput[] = []): HiddenType | undefined {
  if (resolvesReadReceiptGhost(scores, events)) {
    return hiddenType('hidden_read_receipt_ghost');
  }
  if (resolvesGroupChatLawyer(scores, events)) {
    return hiddenType('hidden_group_chat_lawyer');
  }
  if (resolvesScreenshotArchivist(scores, events)) {
    return hiddenType('hidden_screenshot_archivist');
  }
  if (resolvesMemeOverlord(scores, events)) {
    return hiddenType('hidden_meme_overlord');
  }
  if (resolvesPlotTwister(scores, events)) {
    return hiddenType('hidden_plot_twister');
  }
  if (resolvesMidnightReplay(scores, events)) {
    return hiddenType('hidden_midnight_replay');
  }
  return undefined;
}
